/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

/**
 * Shared preference for whether the looping background video plays its audio
 * track. Persisted in localStorage and synced across every mounted consumer
 * (Antechamber + Room) via a custom window event, so toggling it in one surface
 * takes effect everywhere. Defaults to ON — uploaded clips that carry music are
 * meant to be heard; users who prefer silence flip it off once and it sticks.
 */

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'bg_video_sound_enabled_v1';
const SYNC_EVENT = 'bg-video-sound-change';

function readStored(): boolean {
  if (typeof window === 'undefined') return true;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  // Absent → default ON; only an explicit '0' means the user turned it off.
  return raw !== '0';
}

export function useBackgroundVideoSound(): [boolean, (next: boolean) => void] {
  const [enabled, setEnabled] = useState<boolean>(readStored);

  useEffect(() => {
    const sync = () => setEnabled(readStored());
    // In-tab updates from another instance...
    window.addEventListener(SYNC_EVENT, sync);
    // ...and cross-tab updates from the browser.
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SYNC_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const update = (next: boolean) => {
    setEnabled(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      // Ignore quota/security errors — the in-memory state still applies.
    }
    window.dispatchEvent(new Event(SYNC_EVENT));
  };

  return [enabled, update];
}
